import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2, Info, ExternalLink, Eye, EyeOff, Mail, KeyRound, Sparkles,
  Layers, Presentation, Coins, AlertTriangle, ArrowUpRight, Zap,
  ArrowDownRight, ListChecks,
} from "lucide-react";
import { format } from "date-fns";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { passwordStrength } from "@/utils/passwordStrength";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

const TABS = [
  { id: "basic",    label: "Basic Details" },
  { id: "security", label: "Account Security" },
  { id: "usage",    label: "Usage" },
  { id: "billing",  label: "Billing" },
];

function fmtDate(s) {
  try { return s ? format(new Date(s), "MMM d, yyyy") : "—"; }
  catch { return "—"; }
}

function fmtMoney(v) {
  return `$${Number(v || 0).toFixed(2)}`;
}

/**
 * Account Details — full-page settings, four tabs:
 *   /account            → Basic Details
 *   /account?t=security → Account Security
 *   /account?t=usage    → Usage
 *   /account?t=billing  → Billing
 *
 * Reachable from the user-avatar menu in the top-right (no longer in the
 * left sidebar).
 */
export default function AccountDetailsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("t") || "basic";
  const setTab = (id) => setParams({ t: id });

  return (
    <div className="page-container max-w-5xl mx-auto px-4 sm:px-8 py-8" data-testid="account-details-page">
      <div className="flex gap-8">
        {/* Sidebar tabs */}
        <nav className="w-44 flex-shrink-0">
          <ul className="space-y-1">
            {TABS.map(t => (
              <li key={t.id}>
                <button
                  onClick={() => setTab(t.id)}
                  data-testid={`account-tab-${t.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    tab === t.id ? "bg-gray-100 text-gray-900 font-semibold" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex-1 min-w-0">
          {tab === "basic"    && <BasicDetailsTab />}
          {tab === "security" && <AccountSecurityTab />}
          {tab === "usage"    && <UsageTab />}
          {tab === "billing"  && <BillingTab />}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Basic Details tab — email + first/last name (inline edit)
// ─────────────────────────────────────────────────────────────
function BasicDetailsTab() {
  const { user, refreshUser } = useAuth();
  const fullName = user?.name || "";
  const [first, last] = fullName.includes(" ")
    ? [fullName.split(" ").slice(0, -1).join(" "), fullName.split(" ").slice(-1)[0]]
    : [fullName, ""];
  const [editing, setEditing] = useState(false);
  const [firstInput, setFirstInput] = useState(first);
  const [lastInput, setLastInput] = useState(last);
  const [saving, setSaving] = useState(false);

  const initial = (user?.name?.[0] || user?.email?.[0] || "?").toUpperCase();

  const cancel = () => {
    setEditing(false);
    setFirstInput(first);
    setLastInput(last);
  };

  const save = async () => {
    setSaving(true);
    try {
      const newName = `${firstInput} ${lastInput}`.trim();
      await axios.put(`${API}/user/profile`, { name: newName }, { withCredentials: true });
      await refreshUser?.();
      toast.success("Name updated");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="basic-details-tab-content">
      <header>
        <h2 className="text-2xl font-bold text-gray-900 font-['Manrope']">
          Basic Details
        </h2>
        <p className="text-sm text-gray-500 mt-1">Your account identity.</p>
      </header>

      {/* Avatar */}
      <div className="flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt={user?.name || ""} className="w-16 h-16 rounded-full object-cover" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-orange-500 text-white text-2xl font-bold flex items-center justify-center">
            {initial}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900">{user?.name || "—"}</p>
          <p className="text-xs text-gray-500">{user?.email}</p>
        </div>
      </div>

      {/* Email (read-only) */}
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-2">Email</label>
        <p className="text-sm text-gray-600" data-testid="account-email">{user?.email}</p>
      </div>

      {/* Name fields with inline edit toggle */}
      <div className="max-w-md">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-semibold text-gray-900">Name</label>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="account-edit-name-btn"
              className="text-xs font-semibold text-teal-700 hover:text-teal-900 transition-colors"
            >
              Edit name
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">First name</label>
            <Input
              value={firstInput}
              onChange={e => setFirstInput(e.target.value)}
              disabled={!editing}
              className="bg-gray-50 disabled:opacity-100 disabled:cursor-default"
              data-testid="account-first-name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Last name</label>
            <Input
              value={lastInput}
              onChange={e => setLastInput(e.target.value)}
              disabled={!editing}
              className="bg-gray-50 disabled:opacity-100 disabled:cursor-default"
              data-testid="account-last-name"
            />
          </div>

          {editing && (
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={save}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="account-save-name-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save changes"}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancel} disabled={saving}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Account Security tab — auth provider + password change
// ─────────────────────────────────────────────────────────────
function AccountSecurityTab() {
  const { user } = useAuth();
  const isGoogle = user?.auth_provider === "google";
  const [showPwd, setShowPwd] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  // Password strength rules — shared with backend `_check_password_strength`
  // via `utils/passwordStrength.js`. Live 3-tier meter (Weak / Good / Strong).
  const pwdStrength = passwordStrength(newPwd);

  const canSubmit = !savingPwd && curPwd && newPwd && confirmPwd
    && pwdStrength.meets && newPwd === confirmPwd;

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!pwdStrength.meets) { toast.error(pwdStrength.hint || "Password is too weak"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords don't match"); return; }
    setSavingPwd(true);
    try {
      // Backend route is POST (bug: used to be PUT → 405 Method Not Allowed).
      await axios.post(`${API}/auth/change-password`,
        { current_password: curPwd, new_password: newPwd },
        { withCredentials: true });
      toast.success("Password changed");
      setCurPwd(""); setNewPwd(""); setConfirmPwd(""); setPwdOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change password");
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="security-tab-content">
      <header>
        <h2 className="text-2xl font-bold text-gray-900 font-['Manrope']">
          Account Security
        </h2>
        <p className="text-sm text-gray-500 mt-1">How you sign in to Slides by Wokelo.</p>
      </header>

      {/* Sign-in method */}
      <div className="max-w-xl">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Sign-in method</h3>
        <div className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 bg-gray-50/50" data-testid="security-auth-provider">
          <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600">
            {isGoogle ? (
              <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            ) : (
              <Mail className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-900">
              {isGoogle ? "Google account" : "Email & password"}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {isGoogle
                ? `You signed in with Google as ${user?.email}`
                : `Signed in with the email ${user?.email}`}
            </p>
          </div>
        </div>
      </div>

      {/* Password change — email/password accounts only */}
      {!isGoogle ? (
        <div className="pt-2 max-w-xl">
          <div className="flex items-start justify-between gap-6 pb-4 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Password</h3>
              <p className="text-xs text-gray-500 mt-0.5">Update the password used to sign in.</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPwdOpen(o => !o)}
              data-testid="account-change-password-btn"
              className="text-teal-700 border-teal-200 hover:bg-teal-50"
            >
              <KeyRound className="w-3.5 h-3.5 mr-1.5" />
              Change password
            </Button>
          </div>

          {pwdOpen && (
            <form onSubmit={submitPassword} className="mt-4 space-y-3 p-4 rounded-lg border border-gray-200 bg-gray-50/40">
              <Input type={showPwd ? "text" : "password"} placeholder="Current password" value={curPwd} onChange={e => setCurPwd(e.target.value)} className="bg-white" data-testid="security-current-pwd" />
              <Input type={showPwd ? "text" : "password"} placeholder="New password (min 8 chars, letters + numbers)" value={newPwd} onChange={e => setNewPwd(e.target.value)} className="bg-white" data-testid="security-new-pwd" />
              {newPwd && (
                <PasswordStrengthMeter value={newPwd} testid="security-pwd-strength" />
              )}
              <Input type={showPwd ? "text" : "password"} placeholder="Confirm new password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} className="bg-white" data-testid="security-confirm-pwd" />
              {confirmPwd && newPwd !== confirmPwd && (
                <p className="text-[11px] text-red-600">Passwords don't match</p>
              )}
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => setShowPwd(s => !s)} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1">
                  {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {showPwd ? "Hide" : "Show"} passwords
                </button>
                <Button type="submit" size="sm" disabled={!canSubmit} className="bg-teal-600 hover:bg-teal-700" data-testid="security-save-pwd">
                  {savingPwd ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Update password"}
                </Button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <div className="pt-2 max-w-xl">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Password</h3>
          <p className="text-sm text-gray-500">
            Your password is managed by Google. To change it, visit your{" "}
            <a className="text-teal-700 hover:underline" href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer">
              Google account settings
            </a>.
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Usage tab — credit usage bar + lifetime stats
// ─────────────────────────────────────────────────────────────
function UsageTab() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/me/usage`, { withCredentials: true });
        setUsage(r.data);
      } catch {} finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!usage)   return <p className="text-sm text-gray-400">Couldn't load usage right now.</p>;

  const balance  = usage.credits_balance ?? 0;
  const capacity = usage.credits_capacity || 0;
  const isTrial  = usage.account_type === "trial";
  // For paid plans the % bar measures usage against the monthly grant. For
  // trial users we deliberately skip that calculation: their grant is
  // one-shot and a top-up could push the balance above the original 50,
  // which would render nonsensical numbers like "200/50" or "-150% used".
  const used = Math.max(0, capacity - balance);
  const pct  = !isTrial && capacity > 0 ? Math.min(100, Math.round((used / capacity) * 100)) : 0;

  const totalDecks  = usage.total_decks_generated ?? 0;
  const totalSlides = usage.total_slides_generated ?? 0;
  const singles     = usage.total_singles_generated ?? 0;

  return (
    <div className="space-y-10" data-testid="usage-tab-content">
      <header>
        <h2 className="text-2xl font-bold text-gray-900 font-['Manrope']">
          Usage
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Credit consumption and lifetime activity on your account.
        </p>
      </header>

      {/* Credit usage section — paid users see a % bar against monthly
          capacity; trial users only see the running balance to avoid the
          "200/50" math after they top up. */}
      <section data-testid="usage-credits-section">
        {!isTrial && (
          <>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">Credits used</h3>
              <span className="text-xs font-semibold text-gray-500" data-testid="usage-pct-used">
                {pct}% used
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden" aria-label="credits used">
              <div
                className="h-full rounded-full bg-teal-600 transition-all"
                style={{ width: `${pct}%` }}
                data-testid="usage-bar-fill"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {`${usage.plan_name || "Plan"} — ${capacity} credits / month`}
            </p>
          </>
        )}
        {isTrial && (
          <p className="text-xs text-gray-500" data-testid="usage-trial-caption">
            Trial plan — credits roll over and any top-ups are added on top.
          </p>
        )}

        {/* Total credits available card */}
        <div className="mt-5 inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3">
          <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
            <Coins className="w-4.5 h-4.5" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {isTrial ? "Credits remaining" : "Credits available"}
            </p>
            <p className="text-2xl font-extrabold text-gray-900 leading-tight" data-testid="usage-credits-available">
              {balance.toLocaleString()}
            </p>
          </div>
        </div>
      </section>

      {/* Lifetime stats */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Lifetime activity</h3>
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <StatCard
            icon={<Layers className="w-4.5 h-4.5" />}
            label="Decks generated"
            value={totalDecks}
            sublabel="Single + multi-slide combined"
            testId="usage-stat-decks"
          />
          <StatCard
            icon={<Presentation className="w-4.5 h-4.5" />}
            label="Slides generated"
            value={totalSlides}
            sublabel={`Includes ${singles.toLocaleString()} single slides`}
            testId="usage-stat-slides"
          />
        </div>
      </section>

      {/* Credits ledger — same data the admin sees on /admin/users/X/credits.
          Read-only for the user (the row id is omitted server-side so there's
          no handle for void operations). Provides a transparent audit trail
          of every credit movement: trial grants, monthly renewals, top-ups,
          per-generation charges, refunds, and admin adjustments. */}
      <section data-testid="usage-ledger-section">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900">Credits ledger</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Every credit movement on your account — purchases, renewals,
          generation costs, and refunds. Newest first.
        </p>
        <CreditsLedgerTable />
      </section>
    </div>
  );
}

// Credit ledger table — read-only, fetches /api/me/credits/ledger.
// Reuses the admin merge logic so user view stays in lock-step with admin.
function CreditsLedgerTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/me/credits/ledger`, { withCredentials: true });
        setRows(r.data?.rows || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Couldn't load ledger");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (error)   return <p className="text-sm text-red-500">{error}</p>;
  if (!rows.length) return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-8 text-center text-sm text-gray-500" data-testid="ledger-empty">
      No credit activity yet.
    </div>
  );

  const fmtDate = (iso) => {
    try { return format(new Date(iso), "MMM d, yyyy h:mm a"); }
    catch { return iso || "—"; }
  };
  const fmtUsd = (n) => n != null ? `$${Number(n).toFixed(2)}` : "—";

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm" data-testid="credits-ledger-table">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Date</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Reason</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">Description</th>
              <th className="text-right px-4 py-2.5 font-medium text-gray-500 text-xs">Credits</th>
              <th className="text-right px-4 py-2.5 font-medium text-gray-500 text-xs">Amount</th>
              <th className="text-right px-4 py-2.5 font-medium text-gray-500 text-xs">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => {
              const isVoided = !!row.voided_by;
              const isVoidEntry = !!row.voids_id;
              return (
                <tr
                  key={`${row.date}-${i}`}
                  className={`${isVoided ? "opacity-60" : ""} ${isVoidEntry ? "bg-red-50/30" : ""}`}
                  data-testid={`ledger-row-${i}`}
                >
                  <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDate(row.date)}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">{row.reason || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-700">
                    <span className={isVoided ? "line-through text-gray-400" : ""}>
                      {row.description || "—"}
                    </span>
                    {row.status && row.status !== "paid" && row.status !== "complete" && (
                      <span className="ml-2 text-[10px] uppercase font-semibold text-gray-500">{row.status}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-right whitespace-nowrap">
                    {row.delta != null && (
                      <span className={`inline-flex items-center gap-0.5 font-semibold ${row.delta > 0 ? "text-green-700" : "text-red-700"}`}>
                        {row.delta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {row.delta > 0 ? "+" : ""}{row.delta}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-right text-gray-700 whitespace-nowrap">{fmtUsd(row.amount_usd)}</td>
                  <td className="px-4 py-2.5 text-xs text-right text-gray-500 whitespace-nowrap">
                    {row.balance_after != null ? row.balance_after.toLocaleString() : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sublabel, testId }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2.5 text-gray-500">
        <span className="w-7 h-7 rounded-full bg-gray-50 flex items-center justify-center text-gray-600">{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-3xl font-extrabold text-gray-900 mt-3 leading-none">{Number(value || 0).toLocaleString()}</p>
      {sublabel && <p className="text-xs text-gray-500 mt-2">{sublabel}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Billing tab — single Upgrade Plan CTA + Stripe invoices
// ─────────────────────────────────────────────────────────────
function BillingTab() {
  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);

  const reload = async () => {
    try {
      const [b, inv] = await Promise.all([
        axios.get(`${API}/billing/me`, { withCredentials: true }),
        axios.get(`${API}/billing/invoices`, { withCredentials: true }),
      ]);
      setBilling(b.data);
      setInvoices(inv.data?.invoices || []);
    } catch {}
  };

  useEffect(() => {
    (async () => { await reload(); setLoading(false); })();
  }, []);

  const confirmCancel = async () => {
    setCancelling(true);
    try {
      const r = await axios.post(`${API}/billing/cancel-subscription`, {}, { withCredentials: true });
      toast.success(r.data?.message || "Subscription will end at period end.");
      setCancelOpen(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const resumeSub = async () => {
    setResuming(true);
    try {
      await axios.post(`${API}/billing/resume-subscription`, {}, { withCredentials: true });
      toast.success("Subscription resumed — renewals will continue.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't resume subscription");
    } finally {
      setResuming(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const plan = billing?.plan || {};
  const at = billing?.account_type || "trial";
  const isTrial = at === "trial";
  const sub = billing?.subscription || {};
  const cancelPending = !!sub.cancel_at_period_end;
  const periodEndIso = sub.current_period_end
    ? new Date(sub.current_period_end * 1000).toISOString()
    : null;
  const planTitle = isTrial ? "Trial plan" : (plan.name || "Plan");
  const planSubtitle = isTrial
    ? "50 free credits to get you started"
    : `${plan.monthly_credits || 0} credits / month`;

  // Compute the next plan up the ladder (trial → Basic → Pro → Enterprise).
  // Used to render a richer "Upgrade to <Next>" CTA with the next plan's
  // headline price + monthly credit grant.
  // Keep in sync with backend/config.py PLAN_DEFINITIONS.
  const NEXT_PLAN = {
    trial:      { id: "basic",      name: "Basic",      price: "$50/mo",  credits: 100 },
    basic:      { id: "pro",        name: "Pro",        price: "$200/mo", credits: 500 },
    pro:        { id: "enterprise", name: "Enterprise", price: "Contact sales", credits: null },
    enterprise: null,
  };
  const nextPlan = NEXT_PLAN[at] || NEXT_PLAN.trial;

  const openUpgrade = () =>
    window.dispatchEvent(new CustomEvent("credits:open-upgrade", { detail: { tab: "plans" } }));
  const openTopup = () =>
    window.dispatchEvent(new CustomEvent("credits:open-upgrade", { detail: { tab: "topups" } }));

  return (
    <div className="space-y-10" data-testid="billing-tab-content">
      <header>
        <h2 className="text-2xl font-bold text-gray-900 font-['Manrope']">
          Billing
        </h2>
        <p className="text-sm text-gray-500 mt-1">Manage your plan and download invoices.</p>
      </header>

      {/* Cancel-pending banner — shown only when the user has scheduled a
          cancellation. They keep access until the period end and can reverse
          it at any time before then. */}
      {cancelPending && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="billing-cancel-pending-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">Subscription scheduled to cancel</p>
            <p className="text-xs text-amber-800 mt-0.5">
              {periodEndIso
                ? <>You'll keep access and credits until <strong>{fmtDate(periodEndIso)}</strong>. After that, your account drops to the free Trial tier — top-ups still work.</>
                : <>You'll keep access until the end of your current billing period.</>}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={resumeSub}
            disabled={resuming}
            className="flex-shrink-0 border-amber-300 text-amber-900 hover:bg-amber-100"
            data-testid="billing-resume-btn"
          >
            {resuming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Keep subscription"}
          </Button>
        </div>
      )}

      {/* Current plan — header + card with dual CTAs (upgrade / top-up). */}
      <section data-testid="billing-current-plan-section">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3" data-testid="billing-current-plan-header">
          Current plan
        </h3>
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex gap-4 min-w-0">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-100 flex items-center justify-center text-teal-600 flex-shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900" data-testid="billing-plan-title">{planTitle}</h3>
                <p className="text-sm text-gray-500 mt-0.5">{planSubtitle}</p>
                <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                  <div>
                    <span className="text-gray-500">Balance:</span>{" "}
                    <strong className="text-gray-900" data-testid="billing-balance">{billing?.credits_balance ?? 0}</strong>
                  </div>
                  {periodEndIso && (
                    <div>
                      <span className="text-gray-500">{cancelPending ? "Ends:" : "Renews:"}</span>{" "}
                      <strong className="text-gray-900">{fmtDate(periodEndIso)}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Dual CTAs — Upgrade plan (recurring) + Top up credits (one-off).
              Each routes to the matching tab in the pricing modal so the
              user lands on exactly the right page. */}
          <div className="mt-5 pt-5 border-t border-gray-100 flex flex-col sm:flex-row gap-3" data-testid="billing-ctas">
            {nextPlan && (
              <button
                onClick={openUpgrade}
                className="flex-1 group flex items-start gap-3 text-left rounded-lg border border-gray-200 hover:border-gray-900 bg-white hover:bg-gray-50 transition-colors p-4"
                data-testid="billing-upgrade-cta"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    Upgrade to {nextPlan.name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {nextPlan.credits != null
                      ? <>{nextPlan.price} · {nextPlan.credits} credits / month</>
                      : <>{nextPlan.price} · custom credits</>}
                  </p>
                </div>
              </button>
            )}
            <button
              onClick={openTopup}
              className="flex-1 group flex items-start gap-3 text-left rounded-lg border border-gray-200 hover:border-gray-900 bg-white hover:bg-gray-50 transition-colors p-4"
              data-testid="billing-topup-cta"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">Top up credits</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Buy a one-off pack — never expires while your plan is active.
                </p>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Cancel subscription — paid, non-cancel-pending users only */}
      {!isTrial && !cancelPending && (
        <div className="flex items-start justify-between gap-4 pt-2" data-testid="billing-cancel-section">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Cancel subscription</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-md">
              Stop future renewals. You'll keep access and credits until the end of your current billing period, then drop to the free Trial tier. Top-ups remain available.
            </p>
          </div>
          <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-gray-700 border-gray-300 hover:bg-gray-50 hover:text-gray-900 flex-shrink-0"
                data-testid="billing-cancel-btn"
              >
                Cancel subscription
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="cancel-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                <AlertDialogDescription className="space-y-2 pt-1">
                  <span className="block">
                    Stripe will not charge you again. You'll keep your <strong>{plan.name || "current"}</strong> plan
                    {periodEndIso && <> until <strong>{fmtDate(periodEndIso)}</strong></>}, then your account
                    will switch to the free Trial tier.
                  </span>
                  <span className="block text-gray-700">
                    You'll still be able to log in, view your past slides, and buy top-up credits at any time. You can also resume the subscription before the period ends.
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={cancelling} data-testid="cancel-confirm-no">Keep subscription</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); confirmCancel(); }}
                  disabled={cancelling}
                  className="bg-gray-800 hover:bg-gray-900 text-white"
                  data-testid="cancel-confirm-yes"
                >
                  {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Yes, cancel"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Invoices */}
      <div>
        <h3 className="text-base font-bold text-gray-900 mb-4">Invoices</h3>
        {invoices.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="invoices-empty">
            No invoices yet — they'll appear here after your first purchase.
          </p>
        ) : (
          <TooltipProvider delayDuration={200}>
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead>
                <tr className="text-left text-gray-700 border-b border-gray-200">
                  <th className="font-bold py-2 pr-4">Date</th>
                  <th className="font-bold py-2 pr-4">Description</th>
                  <th className="font-bold py-2 pr-4">Total</th>
                  <th className="font-bold py-2 pr-4">Status</th>
                  <th className="font-bold py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={inv.id || i} className="border-b border-gray-100" data-testid={`invoice-row-${i}`}>
                    <td className="py-3 pr-4 text-gray-700">{fmtDate(inv.date)}</td>
                    <td className="py-3 pr-4 text-gray-700">{inv.description}</td>
                    <td className="py-3 pr-4 text-gray-900 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        {fmtMoney(inv.total_usd)}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            <div className="flex justify-between gap-6"><span>Subtotal</span><strong>{fmtMoney(inv.subtotal_usd)}</strong></div>
                            <div className="flex justify-between gap-6"><span>Tax</span><strong>{fmtMoney(inv.tax_usd)}</strong></div>
                            <div className="flex justify-between gap-6 border-t border-gray-200 mt-1 pt-1"><span>Total</span><strong>{fmtMoney(inv.total_usd)}</strong></div>
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs font-semibold ${inv.status === "paid" ? "text-emerald-700" : inv.status === "failed" ? "text-red-600" : "text-amber-600"}`}>
                        {inv.status?.charAt(0).toUpperCase() + inv.status?.slice(1)}
                      </span>
                    </td>
                    <td className="py-3">
                      {inv.invoice_url ? (
                        <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer"
                          className="text-teal-700 hover:text-teal-900 inline-flex items-center gap-1 underline-offset-2 hover:underline"
                          data-testid={`invoice-view-${i}`}>
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
